/**
 * useCalendarPolling — polls Outlook calendar for recently ended appointments.
 *
 * Runs every 5 minutes while the adviser is logged in.
 * Detects financial planning appointments and triggers email prompts.
 *
 * Uses localStorage for persistent tracking of processed events,
 * so duplicate prompts aren't sent across page reloads or sessions.
 *
 * Only active for the adviser (Marinus) — does nothing for the assistant.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  getRecentFinancialPlanningEvents,
  parseCalendarEvent,
  buildConsultationPromptEmail,
} from '../api/calendarApi';
import { sendEmail } from '../api/mailApi';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const PROCESSED_EVENTS_KEY = 'inbox_processed_events';
const MAX_STORED_EVENTS = 200; // Cap stored events to prevent unbounded growth

/**
 * Get the set of already-processed calendar event IDs from localStorage.
 */
function getProcessedEvents() {
  try {
    const data = localStorage.getItem(PROCESSED_EVENTS_KEY);
    if (!data) return new Set();
    const parsed = JSON.parse(data);
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

/**
 * Persist a processed event ID to localStorage.
 */
function markEventProcessed(eventId) {
  const events = getProcessedEvents();
  events.add(eventId);

  // Cap the set size to prevent unbounded growth
  const arr = [...events];
  const trimmed = arr.length > MAX_STORED_EVENTS
    ? arr.slice(arr.length - MAX_STORED_EVENTS)
    : arr;

  localStorage.setItem(PROCESSED_EVENTS_KEY, JSON.stringify(trimmed));
}

/**
 * @param {boolean} enabled — whether polling should be active
 */
export default function useCalendarPolling(enabled = true) {
  const { role, user } = useAuth();
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  const pollCalendar = useCallback(async () => {
    if (role !== 'adviser' || !isMountedRef.current) return;

    try {
      const events = await getRecentFinancialPlanningEvents();
      const processedEvents = getProcessedEvents();

      for (const event of events) {
        // Skip already-processed events (persisted across page reloads)
        if (processedEvents.has(event.id)) continue;

        const eventData = parseCalendarEvent(event);

        // Only process if we extracted a valid client name
        if (!eventData.clientName) {
          // Mark as processed even if we can't extract a name, to avoid re-checking
          markEventProcessed(event.id);
          continue;
        }

        // Mark as processed immediately to avoid duplicates
        markEventProcessed(event.id);

        // Send email prompt to Marinus
        const { subject, body } = buildConsultationPromptEmail(eventData);
        const marinusEmail = process.env.REACT_APP_MARINUS_EMAIL || user?.email;

        if (marinusEmail) {
          try {
            await sendEmail({
              to: marinusEmail,
              subject,
              body,
            });
            console.log(`[CalendarPoll] Sent prompt for: ${eventData.clientName}`);
          } catch (mailErr) {
            console.warn('[CalendarPoll] Failed to send prompt email:', mailErr);
          }
        }
      }
    } catch (err) {
      // Silently handle poll failures — will retry at next interval
      console.warn('[CalendarPoll] Poll failed:', err);
    }
  }, [role, user]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled || role !== 'adviser') return;

    // Initial poll after a brief delay (let the app settle first)
    const initialDelay = setTimeout(pollCalendar, 5000);

    // Set up recurring poll
    intervalRef.current = setInterval(pollCalendar, POLL_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      clearTimeout(initialDelay);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, role, pollCalendar]);

  // Expose manual trigger for testing
  return { pollNow: pollCalendar };
}
